/**
 * DualListPicker Component
 *
 * A dual-list picker for selecting items from a large list.
 * Features two columns (available/selected) with search filtering,
 * single/multi-select, and transfer buttons.
 */

import React, { useState, useMemo } from 'react';
import {
  Input,
  Button,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Search20Regular,
  ChevronRight20Regular,
  ChevronLeft20Regular,
} from '@fluentui/react-icons';

export interface DualListItem {
  id: string;
  label: string;
}

export interface DualListPickerProps {
  availableItems: DualListItem[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  availableLabel?: string;
  selectedLabel?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'stretch',
  },
  listContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: '200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLabel: {
    fontWeight: tokens.fontWeightSemibold,
  },
  count: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  searchInput: {
    width: '100%',
  },
  list: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    height: '200px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  listItem: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  listItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  emptyState: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
  },
});

export const DualListPicker: React.FC<DualListPickerProps> = ({
  availableItems,
  selectedIds,
  onSelectionChange,
  availableLabel = 'Available',
  selectedLabel = 'Selected',
}) => {
  const styles = useStyles();

  // Search state
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');

  // Selection state (items highlighted for transfer)
  const [highlightedAvailable, setHighlightedAvailable] = useState<Set<string>>(new Set());
  const [highlightedSelected, setHighlightedSelected] = useState<Set<string>>(new Set());

  // Compute available and selected items
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const availableItemsFiltered = useMemo(() => {
    return availableItems
      .filter(item => !selectedIdSet.has(item.id))
      .filter(item =>
        availableSearch === '' ||
        item.label.toLowerCase().includes(availableSearch.toLowerCase())
      );
  }, [availableItems, selectedIdSet, availableSearch]);

  const selectedItemsFiltered = useMemo(() => {
    return availableItems
      .filter(item => selectedIdSet.has(item.id))
      .filter(item =>
        selectedSearch === '' ||
        item.label.toLowerCase().includes(selectedSearch.toLowerCase())
      );
  }, [availableItems, selectedIdSet, selectedSearch]);

  // Total counts (unfiltered)
  const availableCount = availableItems.filter(item => !selectedIdSet.has(item.id)).length;
  const selectedCount = selectedIds.length;

  // Handle item click in available list
  const handleAvailableClick = (itemId: string, event: React.MouseEvent) => {
    setHighlightedSelected(new Set()); // Clear other list selection

    if (event.ctrlKey || event.metaKey) {
      // Multi-select
      const newSet = new Set(highlightedAvailable);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      setHighlightedAvailable(newSet);
    } else {
      // Single select
      setHighlightedAvailable(new Set([itemId]));
    }
  };

  // Handle item click in selected list
  const handleSelectedClick = (itemId: string, event: React.MouseEvent) => {
    setHighlightedAvailable(new Set()); // Clear other list selection

    if (event.ctrlKey || event.metaKey) {
      // Multi-select
      const newSet = new Set(highlightedSelected);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      setHighlightedSelected(newSet);
    } else {
      // Single select
      setHighlightedSelected(new Set([itemId]));
    }
  };

  // Handle double-click to transfer immediately
  const handleAvailableDoubleClick = (itemId: string) => {
    onSelectionChange([...selectedIds, itemId]);
    setHighlightedAvailable(new Set());
  };

  const handleSelectedDoubleClick = (itemId: string) => {
    onSelectionChange(selectedIds.filter(id => id !== itemId));
    setHighlightedSelected(new Set());
  };

  // Handle add button click
  const handleAdd = () => {
    const newSelectedIds = [...selectedIds, ...highlightedAvailable];
    onSelectionChange(newSelectedIds);
    setHighlightedAvailable(new Set());
  };

  // Handle remove button click
  const handleRemove = () => {
    const newSelectedIds = selectedIds.filter(id => !highlightedSelected.has(id));
    onSelectionChange(newSelectedIds);
    setHighlightedSelected(new Set());
  };

  return (
    <div className={styles.container}>
      {/* Available List */}
      <div className={styles.listContainer}>
        <div className={styles.header}>
          <Text className={styles.headerLabel}>{availableLabel}</Text>
          <Text className={styles.count}>({availableCount})</Text>
        </div>
        <Input
          className={styles.searchInput}
          placeholder="Search available..."
          contentBefore={<Search20Regular />}
          value={availableSearch}
          onChange={(_, data) => setAvailableSearch(data.value)}
        />
        <div className={styles.list} data-testid="available-list" role="listbox" aria-label={availableLabel}>
          {availableItemsFiltered.length === 0 ? (
            <div className={styles.emptyState}>
              {availableSearch ? 'No matches found' : 'No items available'}
            </div>
          ) : (
            availableItemsFiltered.map(item => (
              <div
                key={item.id}
                role="option"
                tabIndex={0}
                aria-selected={highlightedAvailable.has(item.id)}
                className={`${styles.listItem} ${highlightedAvailable.has(item.id) ? styles.listItemSelected : ''}`}
                data-selected={highlightedAvailable.has(item.id) ? 'true' : 'false'}
                onClick={(e) => handleAvailableClick(item.id, e)}
                onDoubleClick={() => handleAvailableDoubleClick(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAvailableClick(item.id, e as unknown as React.MouseEvent);
                  }
                }}
              >
                {item.label}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Transfer Buttons */}
      <div className={styles.buttonContainer}>
        <Button
          appearance="subtle"
          icon={<ChevronRight20Regular />}
          aria-label="Add selected items"
          disabled={highlightedAvailable.size === 0}
          onClick={handleAdd}
        />
        <Button
          appearance="subtle"
          icon={<ChevronLeft20Regular />}
          aria-label="Remove selected items"
          disabled={highlightedSelected.size === 0}
          onClick={handleRemove}
        />
      </div>

      {/* Selected List */}
      <div className={styles.listContainer}>
        <div className={styles.header}>
          <Text className={styles.headerLabel}>{selectedLabel}</Text>
          <Text className={styles.count}>({selectedCount})</Text>
        </div>
        <Input
          className={styles.searchInput}
          placeholder="Search selected..."
          contentBefore={<Search20Regular />}
          value={selectedSearch}
          onChange={(_, data) => setSelectedSearch(data.value)}
        />
        <div className={styles.list} data-testid="selected-list" role="listbox" aria-label={selectedLabel}>
          {selectedItemsFiltered.length === 0 ? (
            <div className={styles.emptyState}>
              {selectedSearch ? 'No matches found' : 'No items selected'}
            </div>
          ) : (
            selectedItemsFiltered.map(item => (
              <div
                key={item.id}
                role="option"
                tabIndex={0}
                aria-selected={highlightedSelected.has(item.id)}
                className={`${styles.listItem} ${highlightedSelected.has(item.id) ? styles.listItemSelected : ''}`}
                data-selected={highlightedSelected.has(item.id) ? 'true' : 'false'}
                onClick={(e) => handleSelectedClick(item.id, e)}
                onDoubleClick={() => handleSelectedDoubleClick(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectedClick(item.id, e as unknown as React.MouseEvent);
                  }
                }}
              >
                {item.label}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
